import React, { useRef, useState } from 'react'
import { EditorState } from 'draft-js'
import Menu from 'components/menu'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { MenuItem, MenuList } from 'components/menu/items'
import { Attachment, Picture, Plus, Video } from 'components/icons'
import UploadMedia from 'components/editor/components/UploadMedia'
import { INITIAL_CONTEXT_MENU_STATE, OVER_PANEL_Z_INDEX } from 'app-constants'
import { IContextMenuState, IFileParentResource } from 'types'

type MediaType = 'image' | 'video' | 'file'

interface InsertMenuProps {
  editorState: EditorState
  onChange: (newEditorState: EditorState) => void
  resources: IFileParentResource[]
  insertUploadProgress: (
    editorState: EditorState,
    mediaType: MediaType,
    filename: string
  ) => { editorState: EditorState; entityKey: string } | null
  updateUploadProgress: (entityKey: string, progress: number) => void
  replaceUploadProgressWithMedia: (
    progressEntityKey: string,
    mediaType: MediaType,
    mediaData: { url: string; filename?: string; extra?: any } | null
  ) => EditorState
}

const MEDIA: Array<{ type: MediaType; label: string; icon: React.ReactNode }> = [
  { type: 'image', label: 'Photo', icon: <Picture /> },
  { type: 'video', label: 'Video', icon: <Video /> },
  { type: 'file', label: 'Attachment', icon: <Attachment /> }
]

/**
 * The + at the foot of a short rich-text field, with what can be put in it: a photo, a video or an attachment,
 * each uploaded as the document toolbar's buttons upload them (those, hidden here, do the uploading).
 */
const InsertMenu: React.FC<InsertMenuProps> = (props) => {
  const [menuState, setMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const uploads = useRef<HTMLDivElement>(null)

  const pick = (type: MediaType): void => {
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
    uploads.current?.querySelector<HTMLElement>(`[data-media="${type}"]`)?.click()
  }

  return (
    // The + is inset as the text is (12px, less the 4px round its icon), so the two start in line.
    <div className="flex items-center px-2 pb-1.5">
      <Tooltip content="Insert">
        <Button
          internalType="transparent"
          aria-label="Insert"
          aria-haspopup="menu"
          aria-expanded={menuState.open}
          className="size-6 rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-4"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setMenuState({
              ...INITIAL_CONTEXT_MENU_STATE,
              open: true,
              top: `${rect.bottom + 4}px`,
              left: `${rect.left}px`
            })
          }}
        >
          <Plus />
        </Button>
      </Tooltip>
      {menuState.open && (
        // Over a floating panel too, which the field may be in.
        <Menu
          id="editor-insert-menu"
          nested
          menuState={menuState}
          setMenuState={setMenuState}
          width={180}
          zIndex={OVER_PANEL_Z_INDEX}
        >
          <MenuList>
            {MEDIA.map(({ type, label, icon }) => (
              <MenuItem key={type} icon={icon} onClick={() => pick(type)}>
                {label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
      <div ref={uploads} className="hidden">
        {MEDIA.map(({ type }) => (
          <UploadMedia key={type} mediaType={type} {...props} />
        ))}
      </div>
    </div>
  )
}

export default InsertMenu
